import type { Survey } from "@pulse/api/surveyApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faCalendar, faUser } from "@fortawesome/pro-regular-svg-icons";
import StatusBadge from "../SurveyTable/StatusBadge";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ParticipantCountQueryOptions } from "@pulse/api/queryApi";
import { Link } from "@tanstack/react-router";
import { useSelectedCustomerIdParam } from "@/features/hub/selectedCustomerContext";
import {
    Card,
    CardContent,
    CardHeader,
    Progress,
    Separator,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "frontend-components";

export default function SurveyCard(props: Survey) {
    const { i18n, t } = useTranslation();
    const customerId = useSelectedCustomerIdParam();

    const { data: count } = useQuery(ParticipantCountQueryOptions({ surveyId: props.id }));

    const sentOut = props.startedAt ? new Date(props.startedAt) : null;
    const pct = count && count.total > 0 ? Math.round((count.answered / count.total) * 100) : 0;

    return (
        <Card className="shadow-sm">
            <CardHeader className="text-sm font-semibold pl-3 truncate">
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <Link
                                to={
                                    props.status === "DRAFT"
                                        ? "/$customerId/pulse/$pulseId/surveys/$surveyId/edit"
                                        : "/$customerId/pulse/$pulseId/surveys/$surveyId"
                                }
                                className="hover:underline w-full truncate"
                                params={{
                                    customerId,
                                    pulseId: String(props.pulseInstanceId),
                                    surveyId: String(props.id),
                                }}
                            >
                                {props.title}
                            </Link>
                        }
                    />
                    <TooltipContent>{props.title}</TooltipContent>
                </Tooltip>
            </CardHeader>

            <CardContent className="pt-0 px-3 gap-3 flex flex-col items-start">
                <Separator />
                <StatusBadge status={props.status} />
                <div className="text-sm">
                    <FontAwesomeIcon icon={faCalendar} />
                    <span className="ml-1 font-light">
                        {sentOut
                            ? `${t("dashboard.sentOut")}: ${sentOut.toLocaleDateString(
                                  i18n.language,
                                  {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                  },
                              )}`
                            : t("dashboard.notSentOut")}
                    </span>
                </div>
                <div className="text-sm font-light">
                    <FontAwesomeIcon icon={faUser} />
                    <span className="ml-1">
                        {props.createdBy
                            ? `${props.createdBy.firstName} ${props.createdBy.lastName}`
                            : t("common.unknown")}
                    </span>
                </div>
                <div className="w-full">
                    <div className="flex items-center justify-between text-sm font-light">
                        <span>{t("dashboard.responses")}</span>
                        <span>
                            {count?.answered ?? 0}/{count?.total ?? 0} ({pct}%)
                        </span>
                    </div>
                    <Progress value={pct} color="teal-dark" className="mt-1" />
                </div>
            </CardContent>
        </Card>
    );
}
